import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { XCircle, ArrowLeft, HelpCircle, CreditCard } from 'lucide-react';
import { Toaster } from '../../components/Toaster';

// TREATX-CHECKOUT-UX-HARDENING-01 — provider-agnostic failure screen.
// Provider raw error strings are mapped into canonical i18n keys under
// `checkout.errors.*` so patients never see raw PSP vocabulary.
const RAW_REASON_TO_KEY = {
  insufficient_funds: 'insufficient_funds',
  card_declined: 'card_declined',
  expired_card: 'expired_card',
  invalid_card: 'invalid_card',
  processing_error: 'processing_error',
  authentication_required: 'authentication_required',
  cancelled: 'cancelled',
  network: 'network',
};

const PaymentFailedPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawReason = (searchParams.get('reason') || '').toLowerCase();
  const errorKey = RAW_REASON_TO_KEY[rawReason];
  const errorMessage = errorKey
    ? t(`checkout.errors.${errorKey}`)
    : t('checkout.errorGeneric');

  const handleTryAgain = () => {
    navigate(-1);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4"
      data-testid="payment-failed-page"
    >
      <Toaster />

      <Card className="max-w-2xl w-full">
        <CardContent className="pt-6 sm:pt-8 pb-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
              <XCircle className="h-12 w-12 text-red-600" aria-hidden="true" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="payment-failed-title">
              {t('checkout.failed.titleGeneric')}
            </h1>
            <p className="text-gray-600" data-testid="payment-failed-message">
              {errorMessage}
            </p>
          </div>

          <Alert className="mb-6 border-red-200 bg-red-50" role="alert">
            <AlertDescription className="text-sm text-red-900">
              {errorMessage}
            </AlertDescription>
          </Alert>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <HelpCircle className="h-5 w-5" aria-hidden="true" />
              {t('checkout.failed.commonReasons')}
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2"><span aria-hidden="true">•</span><span>{t('checkout.errors.insufficient_funds')}</span></li>
              <li className="flex items-start gap-2"><span aria-hidden="true">•</span><span>{t('checkout.errors.invalid_card')}</span></li>
              <li className="flex items-start gap-2"><span aria-hidden="true">•</span><span>{t('checkout.errors.expired_card')}</span></li>
              <li className="flex items-start gap-2"><span aria-hidden="true">•</span><span>{t('checkout.errors.card_declined')}</span></li>
              <li className="flex items-start gap-2"><span aria-hidden="true">•</span><span>{t('checkout.errors.authentication_required')}</span></li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <CreditCard className="h-5 w-5" aria-hidden="true" />
              {t('checkout.failed.whatToTry')}
            </h3>
            <ol className="space-y-2 text-sm text-blue-900">
              <li className="flex items-start gap-2"><span className="font-semibold flex-shrink-0">1.</span><span>{t('checkout.failed.try1')}</span></li>
              <li className="flex items-start gap-2"><span className="font-semibold flex-shrink-0">2.</span><span>{t('checkout.failed.try2')}</span></li>
              <li className="flex items-start gap-2"><span className="font-semibold flex-shrink-0">3.</span><span>{t('checkout.failed.try3')}</span></li>
              <li className="flex items-start gap-2"><span className="font-semibold flex-shrink-0">4.</span><span>{t('checkout.failed.try4')}</span></li>
            </ol>
          </div>

          <div className="space-y-3">
            <Button
              onClick={handleTryAgain}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              data-testid="payment-failed-retry-btn"
            >
              <CreditCard className="h-4 w-4 mr-2" aria-hidden="true" />
              {t('checkout.failed.retry')}
            </Button>

            <Button
              onClick={() => navigate('/patient/offers')}
              variant="outline"
              className="w-full"
              data-testid="payment-failed-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
              {t('checkout.failed.backToOffers')}
            </Button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {t('checkout.success.supportHint')}
              <a href="mailto:support@treatx.health" className="text-blue-600 hover:underline">support@treatx.health</a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentFailedPage;
